import React, { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import Layout from "../components/layout/Layout";
import Panel from "../components/brand/Panel";
import { useAppToast } from "../components/brand/useAppToast";
import { useAuth } from "../context/AuthContext";
import { getSale, transitionSale } from "../api/salesApi";
import { formatDateTimeUk, formatDateUk } from "../utils/dateDisplay";
import { getApiErrorMessage, isConcurrencyConflict } from "../utils/concurrency";
import { canWriteSales } from "../utils/roleAccess";

const dash = (value) => (value ? value : "—");

const MANAGEMENT = {
  RMC: "Residents' Management Company (RMC)",
  RTM: "Right to Manage Company (RTM)",
};

function propertyAddress(property) {
  return [property.address_line1, property.address_line2, property.city, property.county, property.postcode]
    .filter(Boolean)
    .join("\n");
}

const formPath = (form) => (form.form_type === "FME1" ? `/fme1/${form.id}` : `/lpe1/${form.id}`);

function formHistory(forms) {
  return [...(forms || [])].sort((left, right) =>
    String(right.created_at || "").localeCompare(String(left.created_at || ""))
  );
}

function isThisSaleForm(sale, form) {
  if (form.form_type === "LPE1") return Number(sale.lpe1_form_id) === Number(form.id);
  if (form.form_type === "FME1") return Number(sale.fme1_form_id) === Number(form.id);
  return false;
}

function Field({ label, children }) {
  return (
    <Box sx={{ py: 0.75 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

export default function SaleDetailsPage() {
  const { saleId } = useParams();
  const { user, permissions, roleCapabilities } = useAuth();
  const roles = user?.claims_role || user?.roles || [];
  const canWrite = canWriteSales(roles, permissions, roleCapabilities);
  const { showToast, toastNode } = useAppToast();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [packType, setPackType] = useState("");
  const [packFormId, setPackFormId] = useState("");
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    getSale(saleId)
      .then((payload) => {
        if (!cancelled) setSale(payload);
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err, "This sale could not be loaded."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [saleId]);

  useEffect(() => {
    setNote("");
    setPackType(sale?.pack_form_type || "");
    setPackFormId(sale?.lpe1_form_id ? String(sale.lpe1_form_id) : sale?.fme1_form_id ? String(sale.fme1_form_id) : "");
  }, [sale?.id, sale?.status, sale?.row_version, sale?.pack_form_type, sale?.lpe1_form_id, sale?.fme1_form_id]);

  const move = async (direction, toStatus) => {
    if (direction === "forward" && sale.status === "pack_prepared" && !packFormId) {
      showToast("Link the LPE1 or FME1 form for this unit before sending the pack.", "warning");
      return;
    }
    setSaving(true);
    try {
      const payload = await transitionSale(sale.id, {
        row_version: sale.row_version,
        direction,
        to_status: toStatus || undefined,
        note,
        pack_form_type: packType,
        pack_form_id: packFormId || null,
      });
      setSale(payload);
      showToast(direction === "back" ? "Sale stepped back." : "Sale updated.");
    } catch (err) {
      showToast(getApiErrorMessage(err), isConcurrencyConflict(err) ? "warning" : "error");
    } finally {
      setSaving(false);
      setConfirm(null);
    }
  };

  const ask = (title, body, direction, toStatus) => {
    setConfirm({ title, body, direction, toStatus });
  };

  const formsForType = (sale?.forms || []).filter((form) => form.form_type === packType);
  const history = formHistory(sale?.forms);

  return (
    <Layout eyebrow="Sales" title={sale ? `Sale of ${sale.unit_name || "unit"}` : "Sale"}>
      <Box sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/sales">
          Sales
        </Link>
      </Box>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {sale && !loading && (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.4fr) minmax(280px, 0.8fr)" },
            alignItems: "start",
          }}
        >
          <Box sx={{ order: { xs: 2, md: 1 } }}>
            <Panel title={sale.status_label} eyebrow="Checklist">
              <Box sx={{ px: 2.75, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                {sale.checklist.map((step) => (
                  <Box
                    key={step.key}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "28px minmax(0, 1fr)",
                      gap: 1.5,
                      opacity: step.state === "upcoming" ? 0.55 : 1,
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor: step.state === "upcoming" ? "divider" : "success.main",
                        bgcolor: step.state === "complete" ? "success.main" : "transparent",
                        color: step.state === "complete" ? "common.white" : "success.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                        mt: 0.25,
                      }}
                    >
                      {step.state === "complete" ? "✓" : step.state === "current" ? "•" : ""}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{step.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {step.explanation}
                      </Typography>
                      {step.state === "complete" && (step.completed_by || step.completed_at) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                          Completed by {dash(step.completed_by)} on {formatDateTimeUk(step.completed_at)}
                        </Typography>
                      )}
                      {step.note && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {step.note}
                        </Typography>
                      )}
                      {canWrite && step.state === "current" && (step.actions.length > 0 || step.requires_pack) && (
                        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1.25, maxWidth: 420 }}>
                          {step.requires_pack && (
                            <>
                              <TextField
                                select
                                size="small"
                                label="Pack"
                                value={packType}
                                onChange={(event) => {
                                  setPackType(event.target.value);
                                  setPackFormId("");
                                }}
                              >
                                <MenuItem value="LPE1">LPE1</MenuItem>
                                <MenuItem value="FME1">FME1</MenuItem>
                              </TextField>
                              <TextField
                                select
                                size="small"
                                label="Existing form"
                                value={packFormId}
                                onChange={(event) => setPackFormId(event.target.value)}
                                disabled={!packType}
                              >
                                <MenuItem value="" disabled>Choose a form</MenuItem>
                                {formsForType.map((form) => (
                                  <MenuItem key={`${form.form_type}-${form.id}`} value={String(form.id)}>
                                    {form.form_type} #{form.id} · {form.status}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <Box sx={{ display: "flex", gap: 2 }}>
                                <Link component={RouterLink} to={`/lpe1/new?unit_id=${sale.unit_id}`}>
                                  New LPE1
                                </Link>
                                <Link component={RouterLink} to={`/fme1/new?unit_id=${sale.unit_id}`}>
                                  New FME1
                                </Link>
                              </Box>
                            </>
                          )}
                          <TextField
                            size="small"
                            label="Note"
                            placeholder={step.note_placeholder || "Note (optional)"}
                            value={note}
                            onChange={(event) => setNote(event.target.value)}
                          />
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {step.actions.map((action) => (
                              <Button
                                key={action.to_status}
                                variant={action.kind === "close" && action.to_status === "closed_not_sold" ? "outlined" : "contained"}
                                startIcon={action.kind === "close" && action.to_status === "closed_not_sold" ? <CloseIcon /> : <CheckIcon />}
                                disabled={saving}
                                onClick={() => {
                                  if (action.confirms) {
                                    ask(
                                      action.to_status === "closed_sold"
                                        ? "Close this sale as sold?"
                                        : "Close this sale as not sold?",
                                      "This records the outcome. You can step back to Enquiries if it was chosen by mistake.",
                                      "forward",
                                      action.to_status
                                    );
                                    return;
                                  }
                                  move("forward", action.to_status);
                                }}
                              >
                                {action.label}
                              </Button>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
                {canWrite && sale.step_back && (
                  <Button
                    variant="outlined"
                    startIcon={<UndoIcon />}
                    disabled={saving}
                    onClick={() =>
                      ask(
                        `Step this sale back to ${sale.step_back.label}?`,
                        "Anything already sent or paid is not withdrawn. The earlier step stays in the activity list.",
                        "back",
                        sale.step_back.to_status
                      )
                    }
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Step back
                  </Button>
                )}
                {sale.activity?.length > 0 && (
                  <Box sx={{ pt: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Activity
                    </Typography>
                    {sale.activity.map((row, index) => (
                      <Typography key={`${row.at}-${index}`} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {formatDateTimeUk(row.at)} · {dash(row.by)} · {row.direction === "back" ? "Reverted" : "Moved"} from {row.from_label} to {row.to_label}
                        {row.note ? ` · ${row.note}` : ""}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Panel>
          </Box>
          <Box sx={{ order: { xs: 1, md: 2 } }}>
            {sale.property && (
              <Panel title="Property" eyebrow="Property details" sx={{ mb: 2 }}>
                <Box sx={{ px: 2.75, py: 2 }}>
                  <Field label="Name">
                    <Link component={RouterLink} to={`/properties/${sale.property.id}/details`}>
                      {dash(sale.property.name)}
                    </Link>
                  </Field>
                  <Field label="Address">
                    <Box component="span" sx={{ display: "block", whiteSpace: "pre-line" }}>
                      {propertyAddress(sale.property) || "—"}
                    </Box>
                  </Field>
                  <Field label="Type">{dash(sale.property.property_type)}</Field>
                  <Field label="Estate code">{dash(sale.property.estate_code)}</Field>
                  <Field label="Property manager">{dash(sale.property.property_manager)}</Field>
                  <Field label="Freeholder">{dash(sale.property.freeholder_name)}</Field>
                  <Field label="Freeholder address">
                    <Box component="span" sx={{ display: "block", whiteSpace: "pre-line" }}>
                      {dash(sale.property.freeholder_address)}
                    </Box>
                  </Field>
                  <Field label="Management structure">{MANAGEMENT[sale.property.rtm_rmc_type] || "—"}</Field>
                  <Field label="Management company">{dash(sale.property.rtm_rmc_name)}</Field>
                  <Field label="Take-on date">{formatDateUk(sale.property.take_on_date)}</Field>
                </Box>
              </Panel>
            )}
            <Panel title="Conveyancer" eyebrow="Submitted by the resident">
              <Box sx={{ px: 2.75, py: 2 }}>
                <Field label="Company">{dash(sale.conveyancer_company)}</Field>
                <Field label="Contact">{dash(sale.conveyancer_contact)}</Field>
                <Field label="Email">
                  {sale.conveyancer_email ? (
                    <Link href={`mailto:${sale.conveyancer_email}`}>{sale.conveyancer_email}</Link>
                  ) : (
                    "—"
                  )}
                </Field>
                <Field label="Phone">{dash(sale.conveyancer_phone)}</Field>
                <Field label="Reference">{dash(sale.conveyancer_reference)}</Field>
                <Field label="Payer">{dash(sale.payer_name)}</Field>
                <Field label="Owner">{dash(sale.owner_name)}</Field>
                <Field label="Received">{formatDateUk(sale.received_at)}</Field>
                <Field label="Pack fee">{sale.pack_fee ? `£${sale.pack_fee}` : "—"}</Field>
                <Field label="Unit">
                  {sale.unit_id ? (
                    <Link component={RouterLink} to={`/units/${sale.unit_id}/details`}>
                      {dash(sale.unit_name)}
                    </Link>
                  ) : (
                    "—"
                  )}
                </Field>
                <Field label="Ticket">
                  {sale.ticket
                    ? `#${sale.ticket.id} · ${sale.ticket.status || "—"}${sale.ticket.assignee_name ? ` · ${sale.ticket.assignee_name}` : ""}`
                    : "—"}
                </Field>
                <Field label="Pack on file">{dash(sale.pack_form_type)}</Field>
              </Box>
            </Panel>
            <Panel title="Form history" eyebrow="LPE1 and FME1 for this unit" sx={{ mt: 2 }}>
              <Box sx={{ px: 2.75, py: 2, display: "flex", flexDirection: "column", gap: 0.75 }}>
                {history.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No forms for this unit.
                  </Typography>
                )}
                {history.map((form) => (
                  <Link key={`${form.form_type}-${form.id}`} component={RouterLink} to={formPath(form)}>
                    {form.form_type} #{form.id} · {form.status}
                    {form.created_at ? ` · ${formatDateUk(form.created_at)}` : ""}
                    {isThisSaleForm(sale, form) ? " · This sale" : ""}
                  </Link>
                ))}
              </Box>
            </Panel>
          </Box>
        </Box>
      )}
      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm?.body}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" startIcon={<CloseIcon />} onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            disabled={saving}
            onClick={() => move(confirm.direction, confirm.toStatus)}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {toastNode}
    </Layout>
  );
}
